import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ThemeProvider,
  createTheme,
  AppBar
} from '@mui/material';
import { salaryComponentSettingsApi } from '../../../Data/axios';

const SalaryComponents = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [updateDialog, setUpdateDialog] = useState(false);
  const [salaryComponentData, setSalaryComponentData] = useState({
    name: '',
    type: '1',
    valueType: '1',
    componentValueMonthly: '',
    componentValueWeekly: '',
    componentValueBiWeekly: '',
    componentValueSemiMonthly: ''
  });
  const [salaryComponents, setSalaryComponents] = useState([]);
  const [currentComponent, setCurrentComponent] = useState(null);

  const typeOptions = {
    '1': 'Earnings',
    '2': 'Deductions'
  };

  const valueTypeOptions = {
    '1': 'Fixed',
    '2': 'CTC Percentage',
    '3': 'Basic Percentage',
    '4': 'Variable'
  };

  const options = {
    selectableRows: "none",
    selectToolbarPlacement: "none",
    elevation: 0,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 25, 100],
    tableBody: {
      style: {
        maxHeight: "500px",
        overflowY: "scroll",
      },
    },
    tableHead: {
      style: {
        backgroundColor: "#f2f2f2",
        color: "black",
      },
    },
    rows: {
      style: {
        marginLeft: "5px",
        textAlign: "center",
      },
      selectableRowsOnClick: true,
    },
  };

  const getTheme = () =>
    createTheme({
      palette: {
        mode: "light",
        primary: {
          main: "#2196f3",
        },
        secondary: {
          main: "#03a9f4",
        },
      },
      typography: {
        fontFamily: ["Roboto", "Helvetica", "Arial", "Sans-serif"].join(","),
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
            },
          },
        },
      },
    });

  const fetchSalaryComponents = async () => {
    try {
      const response = await salaryComponentSettingsApi.getAll();
      const mappedComponents = response.data.map((component) => ({
        ...component,
        typeText: typeOptions[component.type],
        valueTypeText: valueTypeOptions[component.value]
      }));
      setSalaryComponents(mappedComponents);
    } catch (error) {
      console.error("Error fetching salary components:", error);
    }
  };

  async function SaveComponent() {
    try {
      const formData = new FormData();
      formData.append("components", salaryComponentData.name); // Match API field
      formData.append("type", salaryComponentData.type);
      formData.append("value", salaryComponentData.valueType);
      formData.append("component_value", salaryComponentData.componentValueMonthly);
      formData.append("component_value_week", salaryComponentData.componentValueWeekly || "0");
      formData.append("component_value_biweek", salaryComponentData.componentValueBiWeekly || "0");
      formData.append("component_value_semi_month", salaryComponentData.componentValueSemiMonthly || "0");

      console.log("FormData being sent:", Object.fromEntries(formData.entries())); // Debugging

      await salaryComponentSettingsApi.add(formData);
      fetchSalaryComponents();
      setOpenDialog(false);
      setSalaryComponentData({
        name: '',
        type: '1',
        valueType: '1',
        componentValueMonthly: '',
        componentValueWeekly: '',
        componentValueBiWeekly: '',
        componentValueSemiMonthly: ''
      });
    } catch (error) {
      console.error("Error adding salary component:", error);
    }
  }


  const updateSalaryComponent = async () => {
    try {
      if (currentComponent) {
        const formData = new FormData();
        formData.append("components", salaryComponentData.name);
        formData.append("type", salaryComponentData.type);
        formData.append("value", salaryComponentData.valueType);
        formData.append("component_value", salaryComponentData.componentValueMonthly);
        formData.append("component_value_week", salaryComponentData.componentValueWeekly || "0");
        formData.append("component_value_biweek", salaryComponentData.componentValueBiWeekly || "0");
        formData.append("component_value_semi_month", salaryComponentData.componentValueSemiMonthly || "0");

        console.log("FormData being sent for update:", Object.fromEntries(formData.entries())); // Debugging

        await salaryComponentSettingsApi.update(currentComponent.id, formData);
        fetchSalaryComponents();
        setUpdateDialog(false);
        setSalaryComponentData({
          name: '',
          type: '1',
          valueType: '1',
          componentValueMonthly: '',
          componentValueWeekly: '',
          componentValueBiWeekly: '',
          componentValueSemiMonthly: ''
        });
        setCurrentComponent(null);
      }
    } catch (error) {
      console.error("Error updating salary component:", error);
    }
  };


  const deleteSalaryComponent = async (id) => {
    try {
      await salaryComponentSettingsApi.delete(id);
      fetchSalaryComponents();
    } catch (error) {
      console.error("Error deleting salary component:", error);
    }
  };

  const handleEdit = (component) => {
    setCurrentComponent(component);
    setSalaryComponentData({
      name: component.components,
      type: component.type,
      valueType: component.value,
      componentValueMonthly: component.component_value,
      componentValueWeekly: component.component_value_week,
      componentValueBiWeekly: component.component_value_biweek,
      componentValueSemiMonthly: component.component_value_semi_month
    });
    setUpdateDialog(true);
  };

  useEffect(() => {
    fetchSalaryComponents();
  }, []);

  return (
    <ThemeProvider theme={getTheme()}>
      <div style={{ padding: '20px' }}>
        <Button variant="contained" color="primary" onClick={() => setOpenDialog(true)}sx={{"&:hover": { backgroundColor: "orange" },}}>
          Add Salary Component
        </Button>

        <TableContainer component={Paper} style={{ marginTop: '20px' }}>
          <Table>
            <TableHead style={options.tableHead.style}>
              <TableRow>
                <TableCell>Component Name</TableCell>
                <TableCell>Component Type</TableCell>
                <TableCell>Component Value</TableCell>
                <TableCell>Value Type</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody style={options.tableBody.style}>
              {salaryComponents.length > 0 ? (
                salaryComponents.map((component) => (
                  <TableRow key={component.id}>
                    <TableCell>{component.components}</TableCell>
                    <TableCell>{component.typeText}</TableCell>
                    <TableCell>
                      Monthly - {component.component_value}, Weekly - {component.component_value_week},<br />
                      Bi-Weekly - {component.component_value_biweek}, Semi-Monthly - {component.component_value_semi_month}
                    </TableCell>
                    <TableCell>{component.valueTypeText}</TableCell>
                    <TableCell>
                      <Button color="primary" onClick={() => handleEdit(component)} variant="outlined" sx={{marginRight:"5px"}}>
                        Edit
                      </Button>
                      <Button color="secondary" onClick={() => deleteSalaryComponent(component.id)} variant="outlined">
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    - No salary components added. -
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
          <AppBar sx={{ position: "relative", bgcolor: "#1d82f5" }}>
            <DialogTitle sx={{color:"white"}}>Add Salary Component</DialogTitle>
          </AppBar>
          <DialogContent>
            <TextField
              fullWidth
              label="Salary Component"
              margin="normal"
              value={salaryComponentData.name}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, name: e.target.value })
              }
            />
            <TextField
              select
              fullWidth
              label="Component Type"
              margin="normal"
              value={salaryComponentData.type}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, type: e.target.value })
              }
            >
              <MenuItem value="1">Earnings</MenuItem>
              <MenuItem value="2">Deductions</MenuItem>
            </TextField>
            <TextField
              select
              fullWidth
              label="Value Type"
              margin="normal"
              value={salaryComponentData.valueType}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, valueType: e.target.value })
              }
            >
              <MenuItem value="1">Fixed</MenuItem>
              <MenuItem value="2">CTC Percentage</MenuItem>
              <MenuItem value="3">Basic Percentage</MenuItem>
              <MenuItem value="4">Variable</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Component Value (Monthly)"
              margin="normal"
              type="number"
              value={salaryComponentData.componentValueMonthly}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, componentValueMonthly: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Component Value (Weekly)"
              margin="normal"
              type="number"
              value={salaryComponentData.componentValueWeekly}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, componentValueWeekly: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Component Value (Bi-Weekly)"
              margin="normal"
              type="number"
              value={salaryComponentData.componentValueBiWeekly}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, componentValueBiWeekly: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Component Value (Semi-Monthly)"
              margin="normal"
              type="number"
              value={salaryComponentData.componentValueSemiMonthly}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, componentValueSemiMonthly: e.target.value })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="secondary" sx={{color:"gray"}}>
              Close
            </Button>
            <Button onClick={SaveComponent} color="primary" variant="contained" sx={{"&:hover": { backgroundColor: "orange" },}}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={updateDialog} onClose={() => setUpdateDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>Update Salary Component</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Salary Component"
              margin="normal"
              value={salaryComponentData.name}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, name: e.target.value })
              }
            />
            <TextField
              select
              fullWidth
              label="Component Type"
              margin="normal"
              value={salaryComponentData.type}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, type: e.target.value })
              }
            >
              <MenuItem value="1">Earnings</MenuItem>
              <MenuItem value="2">Deductions</MenuItem>
            </TextField>
            <TextField
              select
              fullWidth
              label="Value Type"
              margin="normal"
              value={salaryComponentData.valueType}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, valueType: e.target.value })
              }
            >
              <MenuItem value="1">Fixed</MenuItem>
              <MenuItem value="2">CTC Percentage</MenuItem>
              <MenuItem value="3">Basic Percentage</MenuItem>
              <MenuItem value="4">Variable</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Component Value (Monthly)"
              margin="normal"
              type="number"
              value={salaryComponentData.componentValueMonthly}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, componentValueMonthly: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Component Value (Weekly)"
              margin="normal"
              type="number"
              value={salaryComponentData.componentValueWeekly}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, componentValueWeekly: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Component Value (Bi-Weekly)"
              margin="normal"
              type="number"
              value={salaryComponentData.componentValueBiWeekly}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, componentValueBiWeekly: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Component Value (Semi-Monthly)"
              margin="normal"
              type="number"
              value={salaryComponentData.componentValueSemiMonthly}
              onChange={(e) =>
                setSalaryComponentData({ ...salaryComponentData, componentValueSemiMonthly: e.target.value })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUpdateDialog(false)} color="secondary">
              Close
            </Button>
            <Button onClick={updateSalaryComponent} color="primary" variant="contained">
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
};

export default SalaryComponents;
